import { makeStyles } from 'tss-react/mui';
/**
 * Shared styles for the moderation pane sections.
 *
 * The colors are hard-coded rather than taken from theme.palette: the panes in
 * this fork render on a white surface while the MUI palette assumes the dark
 * Jitsi theme, so palette-derived text/background colors come out invisible.
 */
export const useModerationStyles = makeStyles()(() => {
    return {
        card: {
            backgroundColor: '#fff',
            border: '1px solid #E4EBF5',
            borderRadius: 12,
            boxShadow: '0 1px 2px rgba(19, 21, 25, 0.04)',
            padding: 16
        },
        cardHeader: {
            alignItems: 'center',
            display: 'flex',
            gap: 10
        },
        cardIcon: {
            alignItems: 'center',
            backgroundColor: '#EEF3FA',
            borderRadius: 8,
            display: 'flex',
            flexShrink: 0,
            height: 30,
            justifyContent: 'center',
            width: 30
        },
        cardIconWarning: {
            backgroundColor: 'rgba(255, 193, 7, 0.16)'
        },
        cardTitle: {
            color: '#131519',
            flexGrow: 1,
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 0.2,
            lineHeight: '20px'
        },
        cardDescription: {
            color: '#5E6D7A',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: '18px',
            margin: '8px 0 0'
        },
        badge: {
            backgroundColor: 'rgba(255, 193, 7, 0.2)',
            borderRadius: 20,
            color: '#8A6D00',
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.5,
            padding: '3px 8px',
            textTransform: 'uppercase'
        },
        badgeLive: {
            backgroundColor: 'rgba(49, 183, 106, 0.18)',
            color: '#1E7E34'
        },
        row: {
            alignItems: 'center',
            display: 'flex',
            gap: 12,
            justifyContent: 'space-between',
            padding: '12px 0 0'
        },
        rowDivided: {
            borderTop: '1px solid #F0F4F9',
            marginTop: 12
        },
        rowIndented: {
            paddingLeft: 16
        },
        rowText: {
            minWidth: 0
        },
        rowLabel: {
            color: '#131519',
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            lineHeight: '18px'
        },
        rowHint: {
            color: '#5E6D7A',
            display: 'block',
            fontSize: 11.5,
            fontWeight: 400,
            lineHeight: '16px',
            margin: '2px 0 0'
        },
        notice: {
            display: 'block',
            fontSize: 11.5,
            fontWeight: 700,
            lineHeight: '16px',
            margin: '2px 0 0'
        },
        noticeWarning: {
            color: '#B58900'
        },
        noticeError: {
            color: '#D3223A'
        },
        button: {
            alignItems: 'center',
            border: 0,
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex',
            fontSize: 13,
            fontWeight: 700,
            gap: 8,
            justifyContent: 'center',
            marginTop: 12,
            padding: '10px 14px',
            transition: 'background-color .15s ease, border-color .15s ease',
            width: '100%',
            '&:disabled': {
                cursor: 'default',
                opacity: 0.5
            }
        },
        buttonPrimary: {
            backgroundColor: '#4B6790',
            color: '#fff',
            '&:hover': {
                backgroundColor: '#3B5273'
            }
        },
        buttonDanger: {
            backgroundColor: '#D3223A',
            color: '#fff',
            '&:hover': {
                backgroundColor: '#B81D31'
            }
        },
        buttonOutlined: {
            backgroundColor: '#fff',
            border: '1px solid #C7D5E9',
            color: '#4B6790',
            '&:hover': {
                backgroundColor: '#F4F8FD'
            }
        },
        cardHighlighted: {
            backgroundColor: '#FFFBF0',
            borderColor: '#F0DDA8'
        },
        roster: {
            borderTop: '1px solid #F0F4F9',
            margin: '12px 0 0',
            paddingTop: 12
        },
        rosterTitle: {
            color: '#5E6D7A',
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            marginBottom: 4,
            textTransform: 'uppercase'
        },
        rosterHeader: {
            alignItems: 'baseline',
            display: 'flex',
            gap: 8,
            justifyContent: 'space-between'
        },
        rosterLink: {
            background: 'none',
            border: 0,
            color: '#4687ED',
            cursor: 'pointer',
            flexShrink: 0,
            fontSize: 11.5,
            fontWeight: 700,
            padding: 0,
            '&:hover': {
                textDecoration: 'underline'
            }
        },
        rosterMore: {
            background: 'none',
            border: '1px dashed #C7D5E9',
            borderRadius: 8,
            color: '#4B6790',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            marginTop: 6,
            padding: '7px 8px',
            width: '100%',
            '&:hover': {
                backgroundColor: '#F4F8FD'
            }
        },
        rosterText: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            minWidth: 0
        },
        rosterDetail: {
            color: '#5E6D7A',
            fontSize: 11.5,
            lineHeight: '15px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        // The full backstage dialog: a searchable copy of the roster.
        dialogDescription: {
            color: '#5E6D7A',
            fontSize: 13,
            lineHeight: '18px',
            margin: '0 0 12px'
        },
        dialogList: {
            margin: '10px 0 0',
            maxHeight: '50vh',
            overflowX: 'hidden',
            overflowY: 'auto'
        },
        dialogFooter: {
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            marginTop: 16,
            '& > button': {
                marginTop: 0,
                width: 'auto'
            }
        },
        rosterRow: {
            alignItems: 'center',
            borderRadius: 8,
            display: 'flex',
            gap: 8,
            padding: '6px 4px',
            '&:hover': {
                backgroundColor: '#F4F8FD'
            }
        },
        rosterAvatar: {
            alignItems: 'center',
            backgroundColor: '#4B6790',
            borderRadius: '50%',
            color: '#fff',
            display: 'flex',
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 700,
            height: 28,
            justifyContent: 'center',
            textTransform: 'uppercase',
            width: 28
        },
        rosterName: {
            color: '#131519',
            flexGrow: 1,
            fontSize: 13,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        rosterRole: {
            backgroundColor: '#EEF3FA',
            borderRadius: 20,
            color: '#4B6790',
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.3,
            padding: '2px 8px',
            textTransform: 'uppercase'
        },
        rosterButton: {
            backgroundColor: '#31B76A',
            border: 0,
            borderRadius: 6,
            color: '#fff',
            cursor: 'pointer',
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 700,
            padding: '6px 10px',
            '&:hover': {
                backgroundColor: '#28995A'
            }
        },
        tabs: {
            backgroundColor: '#EEF3FA',
            borderRadius: 10,
            display: 'flex',
            gap: 4,
            margin: '10px 0 0',
            padding: 4
        },
        tab: {
            background: 'none',
            border: 0,
            borderRadius: 8,
            color: '#5E6D7A',
            cursor: 'pointer',
            flexGrow: 1,
            fontSize: 12.5,
            fontWeight: 700,
            padding: '8px 10px',
            transition: 'background-color .15s ease, color .15s ease',
            '&:hover': {
                color: '#131519'
            }
        },
        tabActive: {
            backgroundColor: '#fff',
            boxShadow: '0 1px 2px rgba(19, 21, 25, 0.08)',
            color: '#131519'
        },
        rosterHint: {
            color: '#5E6D7A',
            display: 'block',
            fontSize: 11.5,
            lineHeight: '16px',
            margin: '10px 0 6px'
        },
        rosterEmpty: {
            color: '#5E6D7A',
            display: 'block',
            fontSize: 12.5,
            padding: '18px 4px',
            textAlign: 'center'
        },
        // The roster row is a flex line; an open action list has to sit under it
        // rather than inside it, so each entry wraps both.
        rosterEntry: {
            borderBottom: '1px solid #F4F8FD'
        },
        rosterFlags: {
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            gap: 4
        },
        rosterFlagIcon: {
            alignItems: 'center',
            color: '#8A94A0',
            display: 'flex'
        },
        rosterMenuButton: {
            alignItems: 'center',
            background: 'none',
            border: 0,
            borderRadius: 6,
            color: '#5E6D7A',
            cursor: 'pointer',
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'center',
            padding: 4,
            '&:hover': {
                backgroundColor: '#E4EBF5'
            }
        },
        rosterActions: {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: '2px 4px 8px 36px'
        },
        rosterAction: {
            background: 'none',
            border: 0,
            borderRadius: 6,
            color: '#131519',
            cursor: 'pointer',
            fontSize: 12.5,
            fontWeight: 600,
            padding: '7px 8px',
            textAlign: 'left',
            '&:hover': {
                backgroundColor: '#F4F8FD'
            },
            '&:disabled': {
                color: '#A9B4C0',
                cursor: 'default'
            },
            '&:disabled:hover': {
                backgroundColor: 'transparent'
            }
        },
        rosterActionDanger: {
            color: '#D3223A',
            '&:hover': {
                backgroundColor: '#FDF0F2'
            }
        }
    };
});
